import type {DocumentSchema} from "typesense/lib/Typesense/Documents";
import type {SearchParams} from "typesense/lib/Typesense/Types";

/** `cache_ttl` (seconds) embedded into anonymous Typesense scoped keys to force shared server-side caching. */
export const ANONYMOUS_SEARCH_CACHE_TTL_SECONDS = 300;

export const typesenseTypoConfigForProjectsCollection: Pick<
    SearchParams<DocumentSchema>,
    "typo_tokens_threshold" | "num_typos" | "min_len_1typo" | "min_len_2typo"
> = {
    min_len_1typo: 5,
    min_len_2typo: 8,
};

/**
 * Configs for Typesense users search in global site search.
 *
 * We don't configure infix here because it doens't work well
 * with multi search, need to check with Typesense team
 */
export const typesenseConfigForSiteSearchUsers: Pick<
    SearchParams<DocumentSchema>,
    "typo_tokens_threshold" | "num_typos" | "min_len_1typo" | "min_len_2typo" | "drop_tokens_threshold" | "use_cache"
> = {
    min_len_1typo: 4,
    min_len_2typo: 8,
    // Trying to match as many results as possible, but not monopolizing
    // CPU in one query
    drop_tokens_threshold: 10,
    typo_tokens_threshold: 10,

    // Cache for identical search
    use_cache: true,
};

/**
 * Configs for Typesense organizations search in site map.
 *
 * Here we don't configure `drop_tokens_threshold` nor `typo_tokens_threshold` because the
 * search keyword is chosen by us
 */
export const typesenseConfigForSiteMapUsers: Pick<
    SearchParams<DocumentSchema>,
    | "typo_tokens_threshold"
    | "num_typos"
    | "min_len_1typo"
    | "min_len_2typo"
    | "drop_tokens_threshold"
    | "infix"
    | "use_cache"
> = {
    min_len_1typo: 4,
    min_len_2typo: 8,
    infix: ["off", "always"],

    // Cache for identical search
    use_cache: true,
};

/**
 * Configs for Typesense organizations search in global site search.
 *
 * We don't configure infix here because it doens't work well
 * with multi search, need to check with Typesense team
 */
export const typesenseConfigForSiteSearchOrganizations: Pick<
    SearchParams<DocumentSchema>,
    "typo_tokens_threshold" | "num_typos" | "min_len_1typo" | "min_len_2typo" | "drop_tokens_threshold" | "use_cache"
> = {
    min_len_1typo: 4,
    min_len_2typo: 8,
    // Trying to match as many results as possible, but not monopolizing
    // CPU in one query
    drop_tokens_threshold: 10,
    typo_tokens_threshold: 10,

    // Cache for identical search
    use_cache: true,
};

/**
 * Configs for Typesense organizations search in site map.
 *
 * Here we don't configure `drop_tokens_threshold` nor `typo_tokens_threshold` because the
 * search keyword is chosen by us
 */
export const typesenseConfigForSiteMapOrganizations: Pick<
    SearchParams<DocumentSchema>,
    | "typo_tokens_threshold"
    | "num_typos"
    | "min_len_1typo"
    | "min_len_2typo"
    | "drop_tokens_threshold"
    | "infix"
    | "use_cache"
> = {
    min_len_1typo: 4,
    min_len_2typo: 8,
    infix: ["off", "always"],

    // Cache for identical search
    use_cache: true,
};

export const typesenseConfigForProfile: Pick<
    SearchParams<DocumentSchema>,
    "typo_tokens_threshold" | "num_typos" | "min_len_1typo" | "min_len_2typo" | "exhaustive_search"
> = {
    ...typesenseTypoConfigForProjectsCollection,
    // When filtering in user profile page, we want to find and return _ALL_ matches
    exhaustive_search: true,
};

export const typesenseConfigForProfileSearch: Pick<
    SearchParams<DocumentSchema>,
    "typo_tokens_threshold" | "num_typos" | "min_len_1typo" | "min_len_2typo" | "exhaustive_search" | "sort_by"
> = {
    ...typesenseConfigForProfile,
    sort_by: "updated_at:desc",
};

// Only up to 3 sort fields are allowed in a single search query.
// See https://typesense.org/docs/26.0/api/search.html#ranking-and-sorting-parameters
export const typesenseProjectsCollectionDefaultSort: SearchParams<DocumentSchema>["sort_by"] =
    "star_count:desc,_text_match:desc,comment_count:desc";
export const typesenseUsersCollectionDefaultSort: SearchParams<DocumentSchema>["sort_by"] =
    "follower_count:desc,_text_match:desc,parts_count:desc";
export const typesenseOrganizationsCollectionDefaultSort: SearchParams<DocumentSchema>["sort_by"] =
    "follower_count:desc,_text_match:desc,partsCount:desc";

export const typesenseConfigForPartCollection: SearchParams<DocumentSchema> = {
    // Cache for identical search
    use_cache: true,

    /**
     * Below tries to mirror algolia index config in `functions-algolia -> indexConfig.ts`
     *
     * NOTE these are already default behavior in Typesense:
     * - queryType: "prefixLast"
     * - advancedSyntax: true - support double quotes and minus to do exact/exclude search
     * - removeStopWords: false - when we dont setup any stop words in Typesense, this is already the behavior
     */

    // minWordSizefor1Typo in algolia config
    min_len_1typo: 7,
    // minWordSizefor2Typos in algolia config
    min_len_2typo: 12,

    // hitsPerPage in algolia config
    per_page: 10,
    // paginationLimitedTo in algolia config
    limit_hits: 20000,
    // maxValuesPerFacet in algolia config
    max_facet_values: 100,

    // attributesToRetrieve in algolia config
    // `project_url` carries the canonical slug~short_id link used by the sitemap
    // and search UIs to route parts; it must be retrieved or those pages omit
    // every part hit for lack of a routable URL.
    include_fields:
        "archived,description,footprint_count,has_behaviour,layout_count,name,organization_owner_uid,owner_handle,owner_uid,partVersionData,preview_image,project_url,reviewIssueSummary,star_count,three_d_model_count,uid,use_count,version",
    // unretrievableAttributes in algolia config
    exclude_fields: "anyoneCanView,userAccessList",

    // highlightPreTag and highlightPostTag in algolia config
    highlight_start_tag: "<em>",
    highlight_end_tag: "</em>",

    // removeWordsIfNoResults: "none" in algolia config
    drop_tokens_threshold: 0,

    // Bucket results into a few relevance tiers with `text_match_buckets` so that
    // `contentScore` (our curated ranking: pinned generic parts, popularity, quality)
    // ranks parts *within* each tier — mirroring Algolia's "relevance first, then
    // custom ranking" behavior. Without bucketing, `prioritize_token_position` makes
    // `_text_match` almost never tie, so `contentScore` never breaks ties and popular
    // canonical parts (e.g. "Generic Resistor") sink far down the results.
    sort_by: "_text_match(buckets: 4):desc,contentScore:desc,updated_at:desc",

    // Prioritize exact token matches over prefix matches, mirroring Algolia's
    // dedicated "exact" ranking tier.
    prioritize_exact_match: true,
    // Give higher scores when query tokens appear closer together in the document,
    // mirroring Algolia's "proximity" ranking tier.
    prioritize_token_position: true,
    // 200 prefix/typo candidates (vs default 4) for contentScore tie-breaking
    // without the ~50x CPU cost of exhaustive_search (10,000).
    max_candidates: 200,
    // Best-effort safety valve: return results if query exceeds 2s.
    search_cutoff_ms: 2000,

    // allowTyposOnNumericTokens in algoliaConfig
    // TODO: Need to upgrade sdk to support this
    // enable_typos_for_numerical_tokens: false,

    // Not supported in Typesense
    // cameCaseAttributes
    // TODO: See hwo to support these
    // exactOnSingleWordQuery, alternativesAsExact
};
